import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Pencil, Plus, ShieldCheck, ShieldOff } from 'lucide-react';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDisplayDateTime } from '@/utils/date';
import { PCMP_PERMISSION } from '../../api/pcmpAccess.api';
import { pcmpEditPermissionsQueryOptions } from '../../api/pcmpEditPermissions.api';
import { usePcmpPermissions } from '../../hooks/usePcmpPermissions';
import { useSavePcmpEditPermission } from '../../hooks/useSavePcmpEditPermission';
import type { PcmpEditPermissionItem } from '../../types/pcmpEditPermissions.types';
import { PcmpEditPermissionDialog } from './PcmpEditPermissionDialog';
import type { PcmpGrantPickedUser } from './PcmpGrantUserPicker';

function Flag({ on }: { on: boolean }) {
  return on ? <Badge>Granted</Badge> : <span className="text-muted-foreground">-</span>;
}

function ChangedBy({ by, at }: { by: { fullName: string } | null | undefined; at: string }) {
  return (
    <div className="flex flex-col">
      <span>{by?.fullName ?? '-'}</span>
      <span className="text-xs text-muted-foreground">{formatDisplayDateTime(at)}</span>
    </div>
  );
}

/** The PCMP edit grants an Admin has handed to General Users (legacy PCMPEditPermission). */
export function PcmpEditPermissionsTable() {
  const { can, isLoading: isLoadingAccess } = usePcmpPermissions();
  const isAdmin = can(PCMP_PERMISSION.ADMIN);
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    ...pcmpEditPermissionsQueryOptions(),
    enabled: isAdmin,
  });
  const [dialog, setDialog] = useState<{ user: PcmpGrantPickedUser | null } | null>(null);
  const [withdrawing, setWithdrawing] = useState<PcmpEditPermissionItem | null>(null);
  const withdraw = useSavePcmpEditPermission(() => setWithdrawing(null));

  const columns = useMemo<ColumnDef<PcmpEditPermissionItem>[]>(
    () => [
      {
        id: 'user',
        header: 'General User',
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium">{row.original.fullName}</span>
            {row.original.hrEmpId ? (
              <span className="text-xs text-muted-foreground">{row.original.hrEmpId}</span>
            ) : null}
          </div>
        ),
      },
      {
        id: 'editAnyStage',
        header: 'Edit from any stage',
        cell: ({ row }) => <Flag on={row.original.editAnyStage} />,
      },
      {
        id: 'proposalCorrection',
        header: 'Proposal Correction',
        cell: ({ row }) => <Flag on={row.original.proposalCorrection} />,
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) =>
          row.original.isGranted ? (
            <Badge variant="secondary">Active</Badge>
          ) : (
            <span className="text-muted-foreground">Withdrawn</span>
          ),
      },
      {
        id: 'granted',
        header: 'Granted by',
        cell: ({ row }) => <ChangedBy by={row.original.grantedBy} at={row.original.grantedAt} />,
      },
      {
        id: 'updated',
        header: 'Last changed by',
        cell: ({ row }) => <ChangedBy by={row.original.updatedBy} at={row.original.updatedAt} />,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() =>
                setDialog({
                  user: { id: row.original.userId, name: row.original.fullName },
                })
              }
            >
              <Pencil className="size-3.5" />
              Edit
            </Button>
            {row.original.isGranted ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => setWithdrawing(row.original)}
              >
                <ShieldOff className="size-3.5" />
                Withdraw
              </Button>
            ) : null}
          </div>
        ),
      },
    ],
    [],
  );

  if (!isLoadingAccess && !isAdmin) {
    return (
      <p className="text-sm text-muted-foreground">
        PCMP edit permissions can only be managed by an administrator.
      </p>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">Edit Permissions</h1>
          <p className="text-sm text-muted-foreground">
            General Users are read-only in PCMP. Grant chosen ones either or both of the options
            below without changing their user type.
          </p>
        </div>
        <Button type="button" className="gap-1.5" onClick={() => setDialog({ user: null })}>
          <Plus className="size-4" />
          Grant permission
        </Button>
      </div>

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load the edit permissions"
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          className="min-h-0 flex-1"
        />
      ) : (
        <DataTable
          data={data ?? []}
          columns={columns}
          isLoading={isLoading || isLoadingAccess}
          emptyIcon={ShieldCheck}
          emptyMessage="No permissions granted yet"
          className="flex-1 min-h-0"
        />
      )}

      {dialog ? (
        <PcmpEditPermissionDialog user={dialog.user} onClose={() => setDialog(null)} />
      ) : null}

      <ConfirmDialog
        open={withdrawing !== null}
        onOpenChange={(open) => !open && setWithdrawing(null)}
        title="Withdraw permissions"
        description={
          withdrawing
            ? `${withdrawing.fullName} will go back to read-only in PCMP. The record of who granted it stays.`
            : undefined
        }
        confirmLabel="Withdraw"
        confirmVariant="destructive"
        isPending={withdraw.isPending}
        pendingLabel="Withdrawing..."
        onConfirm={() => {
          if (withdrawing) {
            withdraw.mutate({
              userId: withdrawing.userId,
              userName: withdrawing.fullName,
              editAnyStage: false,
              proposalCorrection: false,
            });
          }
        }}
      />
    </div>
  );
}
